import { useEffect, useState } from 'react';
import {
  THEMEN,
  anwenden,
  gespeichertesThema,
  systemBeobachten,
  themaSpeichern,
  type Thema,
} from '../lib/thema';

/**
 * Hell · Dunkel · System.
 *
 * Drei Knöpfe statt eines Kippschalters, weil es drei Zustände sind. Ein
 * Kippschalter kann „folge dem System" nicht ausdrücken — er müsste es
 * verstecken, und dann wählt man beim ersten Klick versehentlich ab, dass die
 * Seite abends mitgeht.
 *
 * Die Sinnbilder stehen als SVG in dieser Datei statt aus einer Bibliothek zu
 * kommen. Drei Symbole rechtfertigen keine Abhängigkeit — und der
 * Loadbalancer hat gar keine Icon-Bibliothek. So ist die Datei in allen drei
 * Oberflächen dieselbe; ein Unterschied müsste sonst gepflegt werden.
 *
 * Beschriftet wird über `title`, nicht über sichtbaren Text: der Schalter sitzt
 * in der Kopfzeile neben Sprache und Konto, und drei Wörter dort sind mehr
 * Gewicht, als die Sache verdient.
 */

const STRICH = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function Sonne() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" {...STRICH}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function Mond() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" {...STRICH}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}

function Bildschirm() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" {...STRICH}>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

const SYMBOLE: Record<Thema, () => JSX.Element> = {
  hell: Sonne,
  dunkel: Mond,
  system: Bildschirm,
};

const NAMEN: Record<Thema, string> = {
  hell: 'Hell',
  dunkel: 'Dunkel',
  system: 'Wie das System',
};

export default function ThemaSchalter() {
  const [thema, setThema] = useState<Thema>(gespeichertesThema);

  // Der Systemeinstellung folgen, solange `system` gewählt ist. Ohne das bliebe
  // die Seite hell, wenn der Rechner abends umschaltet — und genau dafür gibt
  // es diesen Zustand.
  useEffect(() => {
    anwenden(thema);
    return systemBeobachten(() => thema);
  }, [thema]);

  return (
    <div
      role="group"
      aria-label="Darstellung"
      className="inline-flex rounded-lg border border-slate-300 p-0.5"
    >
      {THEMEN.map((t) => {
        const Symbol = SYMBOLE[t];
        const aktiv = thema === t;
        return (
          <button
            key={t}
            type="button"
            title={NAMEN[t]}
            aria-pressed={aktiv}
            onClick={() => {
              setThema(t);
              themaSpeichern(t);
            }}
            className={`rounded-md px-1.5 py-1 transition-colors ${
              aktiv ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <Symbol />
          </button>
        );
      })}
    </div>
  );
}
